/**
 * Unit conversion utilities
 * Provides centralized, cached unit conversion logic to prevent memory leaks
 */

import { UnitOfMeasure } from '@prisma/client'

// Module-level frozen conversion table to prevent per-request allocations
const UNIT_CONVERSIONS = Object.freeze({
  // Length conversions
  METER: Object.freeze({ YARD: 1.09361, FOOT: 3.28084, INCH: 39.37, CENTIMETER: 100, MILLIMETER: 1000 }),
  YARD: Object.freeze({ METER: 0.9144, FOOT: 3, INCH: 36 }),
  FOOT: Object.freeze({ METER: 0.3048, YARD: 0.333333, INCH: 12 }),
  INCH: Object.freeze({ METER: 0.0254, YARD: 0.027778, FOOT: 0.083333 }),
  CENTIMETER: Object.freeze({ METER: 0.01 }),
  MILLIMETER: Object.freeze({ METER: 0.001 }),

  // Area conversions
  SQUAREMETER: Object.freeze({ SQUAREFOOT: 10.7639, SQUAREYARD: 1.19599 }),
  SQUAREFOOT: Object.freeze({ SQUAREMETER: 0.092903, SQUAREYARD: 0.111111 }),
  SQUAREYARD: Object.freeze({ SQUAREMETER: 0.836127, SQUAREFOOT: 9 }),

  // Volume conversions
  LITER: Object.freeze({ GALLON: 0.264172, MILLILITER: 1000 }),
  GALLON: Object.freeze({ LITER: 3.78541, QUART: 4 }),
  MILLILITER: Object.freeze({ LITER: 0.001 }),
  QUART: Object.freeze({ GALLON: 0.25, PINT: 2 }),
  PINT: Object.freeze({ QUART: 0.5 }),

  // Cubic conversions
  CUBIC_METER: Object.freeze({ CUBIC_FOOT: 35.3147, CUBIC_YARD: 1.30795 }),
  CUBIC_FOOT: Object.freeze({ CUBIC_METER: 0.0283168 }),
  CUBIC_YARD: Object.freeze({ CUBIC_METER: 0.764555 }),

  // Unit count types (no conversion)
  UNIT: Object.freeze({ UNIT: 1 }),
  EACH: Object.freeze({ EACH: 1, UNIT: 1 }),
  PIECE: Object.freeze({ PIECE: 1, EACH: 1, UNIT: 1 })
} as const)

// Lazy-initialized cache for conversion results
const conversionCache = new Map<string, number>()

/**
 * Convert a value between units of measure
 * @param value - The numeric value to convert
 * @param from - Source unit of measure
 * @param to - Target unit of measure
 * @returns The converted value
 */
export function convertUnit(value: number, from: string, to: string): number {
  // Skip conversion if units are the same
  if (from === to) return value

  // Check cache first
  const cacheKey = `${from}-${to}`
  let factor = conversionCache.get(cacheKey)

  if (factor === undefined) {
    // Look up conversion factor
    const conversions = UNIT_CONVERSIONS[from as keyof typeof UNIT_CONVERSIONS]
    factor = conversions?.[to as keyof typeof conversions] ?? 1

    // Cache the result
    conversionCache.set(cacheKey, factor)
  }

  return value * factor
}

/**
 * Get the conversion factor between two units (without applying to a value)
 * @param from - Source unit of measure
 * @param to - Target unit of measure
 * @returns The conversion factor
 */
export function getUnitConversionFactor(from: string, to: string): number {
  // Skip conversion if units are the same
  if (from === to) return 1

  // Check cache first
  const cacheKey = `${from}-${to}`
  let factor = conversionCache.get(cacheKey)

  if (factor === undefined) {
    // Look up conversion factor
    const conversions = UNIT_CONVERSIONS[from as keyof typeof UNIT_CONVERSIONS]
    factor = conversions?.[to as keyof typeof conversions] ?? 1

    // Cache the result
    conversionCache.set(cacheKey, factor)
  }

  return factor
}

/**
 * Check if conversion is possible between two units
 * @param from - Source unit of measure
 * @param to - Target unit of measure
 * @returns True if conversion is possible
 */
export function canConvertUnit(from: string, to: string): boolean {
  if (from === to) return true
  const conversions = UNIT_CONVERSIONS[from as keyof typeof UNIT_CONVERSIONS]
  return conversions ? to in conversions : false
}

/**
 * Get all supported units of measure
 * @returns Array of supported unit names
 */
export function getSupportedUnits(): string[] {
  return Object.keys(UNIT_CONVERSIONS)
}

// Export the frozen conversion table for reference (read-only)
export const CONVERSION_TABLE = UNIT_CONVERSIONS

export const UNIT_LABELS: Record<string, string> = {
  FOOT: 'ft',
  YARD: 'yd',
  METER: 'm',
}

export const SWITCHABLE_UNITS = ['FOOT', 'YARD', 'METER'] as const

export function formatUnit(unit: string): string {
  return UNIT_LABELS[unit] ?? unit.toLowerCase()
}

export function convertPricePerUnit(
  pricePerUnit: number,
  nativeUnit: string,
  displayUnit: string
): number {
  if (nativeUnit === displayUnit) return pricePerUnit
  const factor = getUnitConversionFactor(displayUnit, nativeUnit)
  return pricePerUnit * factor
}

export function snapToStep(value: number, step: number): number {
  if (step <= 0) return value
  return Math.round(value / step) * step
}